import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations } from '../translations';

type Language = 'en' | 'es';

/** ISO 3166-1 alpha-2 — Spanish-speaking markets (browser region hint when language list is ambiguous). */
const SPANISH_SPEAKING_REGIONS = new Set([
  'ES', 'MX', 'AR', 'CO', 'CL', 'PE', 'VE', 'EC', 'GT', 'CU', 'BO', 'DO', 'HN', 'PY', 'SV', 'NI', 'CR', 'PA', 'UY', 'PR', 'GQ',
]);

interface LanguageContextType {
  currentLanguage: Language;
  switchLanguage: (language: Language) => void;
  t: (key: string) => string;
  isSpanish: boolean;
  isEnglish: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Helper: saved preference → browser languages → region hint → default English
const detectBrowserLanguage = (): Language => {
  const savedLanguage = localStorage.getItem('chisme-language');
  if (savedLanguage === 'en' || savedLanguage === 'es') {
    return savedLanguage as Language;
  }

  const candidates = [...(navigator.languages ?? []), navigator.language].filter(Boolean);

  for (const lang of candidates) {
    const lower = lang.toLowerCase();
    if (lower.startsWith('es')) return 'es';
  }
  for (const lang of candidates) {
    const lower = lang.toLowerCase();
    if (lower.startsWith('en')) return 'en';
  }

  try {
    const primary = candidates[0] ?? 'en';
    const loc = new Intl.Locale(primary).maximize();
    if (loc.region && SPANISH_SPEAKING_REGIONS.has(loc.region)) return 'es';
  } catch {
    /* ignore */
  }

  return 'en';
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const [currentLanguage, setCurrentLanguage] = useState<Language>(detectBrowserLanguage);

  useEffect(() => {
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  // Update localStorage when language changes
  useEffect(() => {
    localStorage.setItem('chisme-language', currentLanguage);
  }, [currentLanguage]);

  const switchLanguage = (newLanguage: Language): void => {
    if (newLanguage === 'en' || newLanguage === 'es') {
      setCurrentLanguage(newLanguage);
    }
  };

  // Translation function with fallback
  const t = (key: string): string => {
    const keys = key.split('.');
    let value: any = translations[currentLanguage];
    
    // Navigate through the nested object
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English if translation is missing
        value = translations['en'];
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            return `[Missing: ${key}]`; // Return missing key indicator
          }
        }
        break;
      }
    }
    
    return value || `[Missing: ${key}]`;
  };

  const value: LanguageContextType = {
    currentLanguage,
    switchLanguage,
    t,
    isSpanish: currentLanguage === 'es',
    isEnglish: currentLanguage === 'en'
  };

  return React.createElement(
    LanguageContext.Provider,
    { value },
    children
  );
};

// Custom hook to use the language context
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
